/**
 * Battle engine — the turn-resolution core. PURE + deterministic: no rendering, no
 * IO, no network. Every random gate (accuracy, crit, the damage spread, status rolls,
 * catch shakes) flows through an injected `Rng` (core/rng) so a battle replays bit-for-bit
 * from its seed. The renderer never owns rules — `resolveTurn` returns a new `BattleState`
 * plus an ordered `BattleEvent[]` the scenes animate.
 *
 * It integrates the wave-1 modules rather than re-deriving their maths:
 *   - core/stats     — computeStats (A/D + Speed off base/IV/EV/nature/level)
 *   - core/damage    — computeDamage (real damage formula) + rollCritical
 *   - core/typeChart — effectivenessAgainst (for the emitted effectiveness multiplier)
 *   - core/status    — applyStatus / canMove / endOfTurnEffects / effectiveSpeed / thaw
 *   - core/catch     — attemptCatch (wild capture; failure is "it broke free", never a loss)
 *   - core/abilities — getAbilityHooks (Intimidate, Levitate, absorbs, Static, Sturdy, …)
 *
 * Turn order within a turn:
 *   1. switches (player/opponent), firing on-switch-in abilities;
 *   2. items (a wild catch attempt ends the turn on success);
 *   3. moves, ordered by priority → effective Speed (paralysis-halved + speed stage),
 *      with an rng coin-flip on a true tie.
 * After each action we check for faints + forced replacement; end-of-turn applies
 * residual status damage (burn/poison/toxic) and re-checks for a finished battle.
 *
 * Level-up / evolution are POST-battle concerns (see core/leveling + core/evolution);
 * this engine emits no level-up events itself.
 */
import type {
  BattleState,
  BattleAction,
  TurnResult,
  BattleSide,
  BattleSideId,
  BattleParticipant,
  BattleEvent,
  BattleKind,
  SpeciesData,
  MoveData,
  Nature,
  ComputedStats,
  PokemonType,
  StatusCondition,
  PokemonInstance,
} from '@/shared';
import type { Rng } from './rng.js';
import { computeStats, getNature } from './stats.js';
import { computeDamage, rollCritical, type DamageContext } from './damage.js';
import { effectivenessAgainst } from './typeChart.js';
import {
  applyStatus,
  canMove,
  endOfTurnEffects,
  effectiveSpeed,
  thawFromFireMove,
} from './status.js';
import { attemptCatch, type CatchTarget } from './catch.js';
import { getAbilityHooks } from './abilities.js';

/**
 * Read-only data lookups the pure engine needs (species + move records). Injected so
 * the engine stays free of any global dataset/IO. Both lookups must be total for the
 * ids present on the battling instances.
 */
export interface BattleDeps {
  getSpecies(id: string): SpeciesData;
  getMove(id: string): MoveData;
  /**
   * Optional Cozy↔Spicy combat tuning, applied ON TOP of the faithful formulas
   * (never replacing them). All default to neutral (1) when omitted, so Mild and
   * any caller that ignores difficulty get the exact mainline behaviour.
   *   - `foeDamageMultiplier`    scales damage the OPPONENT side deals (≤ 1 in Cozy);
   *   - `playerDamageMultiplier` scales damage the PLAYER side deals (≥ 1 in Cozy);
   *   - `catchRateMultiplier`    eases the species capture rate fed to the catch
   *     formula (≥ 1 in Cozy) — the catch maths + its 255 ceiling are untouched.
   * Source of truth: core/difficulty.ts.
   */
  readonly foeDamageMultiplier?: number;
  readonly playerDamageMultiplier?: number;
  readonly catchRateMultiplier?: number;
}

/** The Cozy-side damage multiplier for a given attacking side (neutral by default). */
function difficultyDamageMultiplier(deps: BattleDeps, attackerSide: BattleSideId): number {
  const m = attackerSide === 'player' ? deps.playerDamageMultiplier : deps.foeDamageMultiplier;
  return typeof m === 'number' && m > 0 ? m : 1;
}

const STAGE_KEYS = ['atk', 'def', 'spatk', 'spdef', 'spe', 'acc', 'eva'] as const;
const ZERO_STAGES: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(STAGE_KEYS.map((k) => [k, 0])),
);

/** Moves that make physical contact (drives Static / contact-ability hooks). */
const CONTACT_CATEGORIES: ReadonlySet<MoveData['category']> = new Set(['physical']);

// ── Working state (a deep, mutation-safe clone of the input) ─────────────────

/** Clone a participant so the engine mutates a working copy, never the caller's data. */
function cloneParticipant(p: BattleParticipant): BattleParticipant {
  return {
    instanceId: p.instanceId,
    instance: {
      ...p.instance,
      ivs: { ...p.instance.ivs },
      evs: { ...p.instance.evs },
      moves: p.instance.moves.map((m) => ({ ...m })),
    },
    statStages: { ...ZERO_STAGES, ...p.statStages },
    volatile: { ...p.volatile },
  };
}

function cloneSide(s: BattleSide): BattleSide {
  return {
    id: s.id,
    trainerName: s.trainerName,
    party: s.party.map(cloneParticipant),
    activeIndex: s.activeIndex,
  };
}

function cloneState(state: BattleState): BattleState {
  return {
    kind: state.kind,
    sides: {
      player: cloneSide(state.sides.player),
      opponent: cloneSide(state.sides.opponent),
    },
    turn: state.turn,
    field: { ...state.field },
    rngSeed: state.rngSeed,
    finished: state.finished,
    winner: state.winner,
  };
}

// ── Lookups + derived stats ──────────────────────────────────────────────────

const natureCache = new Map<string, Nature>();
function natureFor(id: string): Nature {
  let n = natureCache.get(id);
  if (!n) {
    n = getNature(id);
    natureCache.set(id, n);
  }
  return n;
}

function statsOf(p: BattleParticipant, deps: BattleDeps): ComputedStats {
  const species = deps.getSpecies(p.instance.speciesId);
  return computeStats(
    species.baseStats,
    p.instance.ivs,
    p.instance.evs,
    p.instance.level,
    natureFor(p.instance.natureId),
  );
}

function typesOf(p: BattleParticipant, deps: BattleDeps): readonly PokemonType[] {
  return deps.getSpecies(p.instance.speciesId).types;
}

function asDefenderTuple(
  types: readonly PokemonType[],
): readonly [PokemonType] | readonly [PokemonType, PokemonType] {
  return types.length >= 2 ? [types[0]!, types[1]!] : [types[0]!];
}

/** The standard combat stat-stage multiplier (used here only for the Speed stage). */
function stageMultiplier(stage: number): number {
  const s = Math.max(-6, Math.min(6, stage));
  return s >= 0 ? (2 + s) / 2 : 2 / (2 - s);
}

function activeOf(side: BattleSide): BattleParticipant | undefined {
  return side.party[side.activeIndex];
}

/**
 * Copy a fresh instance's mutable fields onto a participant's existing instance IN
 * PLACE. `BattleParticipant.instance` is a readonly *reference* (can't be reassigned),
 * but its fields are mutable — the contract has the engine mutate HP/status on the
 * working copy. The wave-1 status/catch helpers return fresh instances; this threads
 * those values back without breaking the readonly reference.
 */
function assignInstance(p: BattleParticipant, fresh: PokemonInstance): void {
  Object.assign(p.instance, fresh);
}

function isFainted(p: BattleParticipant | undefined): boolean {
  return !p || p.instance.currentHp <= 0;
}

function sideHasUsable(side: BattleSide): boolean {
  return side.party.some((p) => p.instance.currentHp > 0);
}

function other(side: BattleSideId): BattleSideId {
  return side === 'player' ? 'opponent' : 'player';
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Build the initial battle state from both sides + a seed. The state is a deep clone
 * of the inputs so the caller's data is never mutated; stat stages are normalized to a
 * full −0 spread and `turn` starts at 0 (the first `resolveTurn` bumps it to 1).
 */
export function startBattle(
  kind: BattleKind,
  playerSide: BattleSide,
  opponentSide: BattleSide,
  rngSeed: number,
  _deps?: BattleDeps,
): BattleState {
  return {
    kind,
    sides: {
      player: cloneSide({ ...playerSide, id: 'player' }),
      opponent: cloneSide({ ...opponentSide, id: 'opponent' }),
    },
    turn: 0,
    field: {},
    rngSeed: rngSeed >>> 0,
    finished: false,
  };
}

/** Back-compat alias (the stub named it createBattle). */
export const createBattle = startBattle;

/** Both sides' chosen actions for one turn. */
export interface TurnActions {
  readonly player: BattleAction;
  readonly opponent: BattleAction;
}

/**
 * Resolve one full turn. Returns the next `BattleState` + the ordered `BattleEvent[]`
 * a renderer animates. Pure: operates on a clone, threading every roll through `rng`.
 *
 * Overloaded for ergonomics: pass either a `{ player, opponent }` actions object, or
 * the two actions positionally.
 */
export function resolveTurn(
  state: BattleState,
  actions: TurnActions,
  rng: Rng,
  deps: BattleDeps,
): TurnResult;
export function resolveTurn(
  state: BattleState,
  playerAction: BattleAction,
  opponentAction: BattleAction,
  rng: Rng,
  deps: BattleDeps,
): TurnResult;
export function resolveTurn(
  state: BattleState,
  a: TurnActions | BattleAction,
  b: Rng | BattleAction,
  c: BattleDeps | Rng,
  d?: BattleDeps,
): TurnResult {
  let playerAction: BattleAction;
  let opponentAction: BattleAction;
  let rng: Rng;
  let deps: BattleDeps;
  if (isActions(a)) {
    playerAction = a.player;
    opponentAction = a.opponent;
    rng = b as Rng;
    deps = c as BattleDeps;
  } else {
    playerAction = a;
    opponentAction = b as BattleAction;
    rng = c as Rng;
    deps = d as BattleDeps;
  }

  const next = cloneState(state);
  const events: BattleEvent[] = [];

  if (next.finished) {
    return { state: next, events };
  }

  next.turn += 1;
  events.push({ kind: 'turn-start', turn: next.turn });

  const chosen: Record<BattleSideId, BattleAction> = {
    player: playerAction,
    opponent: opponentAction,
  };

  // ── Phase 1: switches (both, player first), firing on-switch-in abilities. ──
  for (const sideId of ['player', 'opponent'] as const) {
    const act = chosen[sideId];
    if (act.type === 'switch') {
      doSwitch(next, sideId, act.partyIndex, events, deps);
    }
  }

  // ── Phase 2: items (a wild catch ends the turn on success). ──
  for (const sideId of ['player', 'opponent'] as const) {
    const act = chosen[sideId];
    if (act.type === 'item') {
      const ended = doItem(next, sideId, act, events, rng, deps);
      if (ended) return { state: next, events };
    }
    if (act.type === 'run') {
      // Wild flee handling is the scene's concern; the engine just notes it.
      events.push({ kind: 'message', text: 'Got away safely!' });
      next.finished = true;
      next.winner = 'draw';
      events.push({ kind: 'battle-end', winner: 'draw', kindOfBattle: next.kind });
      return { state: next, events };
    }
  }

  // ── Phase 3: moves, ordered by priority → effective Speed → rng tie-break. ──
  const movers = (['player', 'opponent'] as const).filter((s) => chosen[s].type === 'move');
  const order = orderMovers(next, movers, chosen, rng, deps);

  // The mon that chose each move (so a replacement forced in mid-turn — whose original
  // active fainted before it could act — does NOT get to move on the same turn).
  const moverInstance: Partial<Record<BattleSideId, string>> = {};
  for (const sideId of movers) {
    moverInstance[sideId] = activeOf(next.sides[sideId])?.instanceId;
  }

  for (const sideId of order) {
    if (next.finished) break;
    const act = chosen[sideId];
    if (act.type !== 'move') continue;
    const active = activeOf(next.sides[sideId]);
    // Skip if the chooser fainted, or was swapped out for a forced replacement.
    if (isFainted(active) || active!.instanceId !== moverInstance[sideId]) continue;
    executeMove(next, sideId, act, events, rng, deps);
    settleFaints(next, events, deps);
  }

  if (next.finished) return { state: next, events };

  // ── End of turn: residual status damage (burn/poison/toxic) + toxic escalation. ──
  applyEndOfTurn(next, events);
  settleFaints(next, events, deps);

  return { state: next, events };
}

function isActions(x: TurnActions | BattleAction): x is TurnActions {
  return (x as TurnActions).player !== undefined && (x as TurnActions).opponent !== undefined;
}

// ── Phase 1: switching ────────────────────────────────────────────────────────

function doSwitch(
  state: BattleState,
  sideId: BattleSideId,
  partyIndex: number,
  events: BattleEvent[],
  deps: BattleDeps,
): void {
  const side = state.sides[sideId];
  const target = side.party[partyIndex];
  if (!target || target.instance.currentHp <= 0 || partyIndex === side.activeIndex) {
    return; // illegal switch — ignore (no event)
  }
  // Reset the outgoing mon's volatile/stage state on switch-out.
  const outgoing = side.party[side.activeIndex];
  if (outgoing) {
    outgoing.statStages = { ...ZERO_STAGES };
    outgoing.volatile = {};
  }
  side.activeIndex = partyIndex;
  events.push({ kind: 'switch', side: sideId, toInstanceId: target.instanceId });
  fireSwitchIn(state, sideId, events, deps);
}

/** Fire the incoming mon's on-switch-in ability (e.g. Intimidate) against the foe. */
function fireSwitchIn(
  state: BattleState,
  sideId: BattleSideId,
  events: BattleEvent[],
  _deps: BattleDeps,
): void {
  const self = activeOf(state.sides[sideId]);
  if (!self) return;
  const hooks = getAbilityHooks(self.instance.abilityId);
  if (!hooks.onSwitchIn) return;
  const foeSide = state.sides[other(sideId)];
  const foe = activeOf(foeSide);
  const opponents = foe ? [foe] : [];
  const effects = hooks.onSwitchIn({ self, opponents });
  for (const eff of effects) {
    const targetSideId = foe && eff.targetInstanceId === foe.instanceId ? other(sideId) : sideId;
    applyStatStage(state, targetSideId, eff.targetInstanceId, eff.stat, eff.stages, events);
  }
}

// ── Phase 2: items / catching ─────────────────────────────────────────────────

/**
 * Handle an item action. The only item the engine models is a ball throw against a
 * wild opponent: on a catch it emits `catch-attempt` + `battle-end` (player wins) and
 * returns true (turn ends immediately). A failed catch is "it broke free", never a loss.
 */
function doItem(
  state: BattleState,
  sideId: BattleSideId,
  act: Extract<BattleAction, { type: 'item' }>,
  events: BattleEvent[],
  rng: Rng,
  deps: BattleDeps,
): boolean {
  const isBall = act.itemId.includes('ball');
  if (sideId !== 'player' || state.kind !== 'wild' || !isBall) {
    return false; // non-catch items are a later concern; ignore quietly
  }
  const wild = activeOf(state.sides.opponent);
  if (!wild) return false;

  const species = deps.getSpecies(wild.instance.speciesId);
  // Cozy eases catching by lifting the capture-rate INPUT; the catch formula and
  // its 255-guaranteed ceiling stay untouched (we clamp the eased rate to 255).
  const catchMult =
    typeof deps.catchRateMultiplier === 'number' && deps.catchRateMultiplier > 0
      ? deps.catchRateMultiplier
      : 1;
  const easedCaptureRate = Math.min(255, Math.round(species.captureRate * catchMult));
  const target: CatchTarget = {
    maxHp: wild.instance.maxHp,
    currentHp: wild.instance.currentHp,
    status: wild.instance.status,
    captureRate: easedCaptureRate,
  };
  const ball = ballIdFromItem(act.itemId);
  const result = attemptCatch(target, ball, rng);
  events.push({
    kind: 'catch-attempt',
    instanceId: wild.instanceId,
    shakes: result.shakes,
    caught: result.caught,
  });

  if (result.caught) {
    state.finished = true;
    state.winner = 'player';
    events.push({ kind: 'battle-end', winner: 'player', kindOfBattle: state.kind });
    return true;
  }
  return false;
}

type Ball = 'poke' | 'great' | 'ultra' | 'net' | 'dusk' | 'master';
function ballIdFromItem(itemId: string): Ball {
  const id = itemId.toLowerCase();
  if (id.includes('master')) return 'master';
  if (id.includes('ultra')) return 'ultra';
  if (id.includes('great')) return 'great';
  if (id.includes('net')) return 'net';
  if (id.includes('dusk')) return 'dusk';
  return 'poke';
}

// ── Phase 3: move ordering + execution ─────────────────────────────────────────

function orderMovers(
  state: BattleState,
  movers: readonly BattleSideId[],
  chosen: Record<BattleSideId, BattleAction>,
  rng: Rng,
  deps: BattleDeps,
): BattleSideId[] {
  if (movers.length < 2) return [...movers];
  const [a, b] = movers as [BattleSideId, BattleSideId];
  const pa = priorityOf(state, a, chosen[a], deps);
  const pb = priorityOf(state, b, chosen[b], deps);
  if (pa !== pb) return pa > pb ? [a, b] : [b, a];

  const sa = effSpeedOf(state, a, deps);
  const sb = effSpeedOf(state, b, deps);
  if (sa !== sb) return sa > sb ? [a, b] : [b, a];

  // True speed tie → coin flip (mainline behavior).
  return rng.int(2) === 0 ? [a, b] : [b, a];
}

function priorityOf(
  state: BattleState,
  sideId: BattleSideId,
  act: BattleAction,
  deps: BattleDeps,
): number {
  if (act.type !== 'move') return 0;
  const active = activeOf(state.sides[sideId]);
  if (!active) return 0;
  const slot = active.instance.moves[act.moveSlot];
  if (!slot) return 0;
  return deps.getMove(slot.moveId).priority;
}

function effSpeedOf(state: BattleState, sideId: BattleSideId, deps: BattleDeps): number {
  const active = activeOf(state.sides[sideId]);
  if (!active) return 0;
  const base = statsOf(active, deps).spe;
  const staged = Math.floor(base * stageMultiplier(numStage(active, 'spe')));
  return effectiveSpeed(staged, active.instance.status);
}

function numStage(p: BattleParticipant, stat: string): number {
  const v = p.statStages[stat];
  return typeof v === 'number' ? v : 0;
}

/** Execute one mon's chosen move: action-gate → accuracy → damage/effects → PP. */
function executeMove(
  state: BattleState,
  sideId: BattleSideId,
  act: Extract<BattleAction, { type: 'move' }>,
  events: BattleEvent[],
  rng: Rng,
  deps: BattleDeps,
): void {
  const attacker = activeOf(state.sides[sideId]);
  if (!attacker) return;
  const slot = attacker.instance.moves[act.moveSlot];
  if (!slot) return;
  // Skip only a genuinely *exhausted* move (real maxPp, but pp spent down to 0).
  // A slot with maxPp <= 0 is *unhydrated* — a data defect (e.g. a sim-trainer mon
  // that reached battle without PP hydration), not a spent move. Rather than freeze
  // the turn into a silent no-op (which makes the opponent never attack), we let it
  // fire as a safety net. Real battles hydrate PP up front (see gameSession
  // .friendlyParty / movesForSpecies), so this branch is belt-and-braces only.
  if (slot.maxPp > 0 && slot.pp <= 0) return;

  // Status action-gate (sleep/freeze/paralysis): may block + may transition.
  const gate = canMove(attacker.instance, rng);
  attacker.instance.status = gate.updatedStatus;
  attacker.instance.statusTurns = gate.updatedStatusTurns;
  if (!gate.canMove) {
    events.push({ kind: 'message', text: blockedMessage(gate.reason) });
    return;
  }

  const move = deps.getMove(slot.moveId);
  slot.pp = Math.max(0, slot.pp - 1);
  events.push({
    kind: 'move-used',
    side: sideId,
    instanceId: attacker.instanceId,
    moveId: move.id,
  });

  const defSideId = other(sideId);
  const defender = activeOf(state.sides[defSideId]);
  if (!defender) return;

  // A *damaging* Fire move thaws a frozen defender (status Fire moves don't),
  // before the accuracy check.
  if (move.type === 'fire' && move.category !== 'status') {
    const thaw = thawFromFireMove(defender.instance, move.type);
    if (thaw.thawed) assignInstance(defender, thaw.instance);
  }

  // Accuracy gate: null accuracy never misses (no roll consumed).
  if (move.accuracy != null) {
    if (rng.int(100) >= move.accuracy) {
      events.push({
        kind: 'move-missed',
        side: sideId,
        instanceId: attacker.instanceId,
        moveId: move.id,
      });
      return;
    }
  }

  if (move.category === 'status') {
    applyMoveEffects(
      state,
      sideId,
      defSideId,
      attacker,
      defender,
      move,
      events,
      rng,
      deps,
      /*hit*/ true,
      /*madeContact*/ false,
    );
    return;
  }

  // ── Damaging move ──
  const aStats = statsOf(attacker, deps);
  const dStats = statsOf(defender, deps);
  const aTypes = typesOf(attacker, deps);
  const dTypes = typesOf(defender, deps);
  const effectiveness = effectivenessAgainst(move.type, asDefenderTuple(dTypes));

  // Defensive try-hit abilities (Levitate / Flash Fire / Water/Volt Absorb).
  const defHooks = getAbilityHooks(defender.instance.abilityId);
  if (defHooks.onTryHit) {
    const tryHit = defHooks.onTryHit({
      defender,
      moveType: move.type,
      baseEffectiveness: effectiveness,
    });
    if (tryHit.immune) {
      if (tryHit.grantsFlashFire) {
        defender.volatile = { ...defender.volatile, flashFire: true };
      }
      if (tryHit.healAmount && tryHit.healAmount > 0) {
        const healed = Math.min(
          defender.instance.maxHp,
          defender.instance.currentHp + tryHit.healAmount,
        );
        const amount = healed - defender.instance.currentHp;
        defender.instance.currentHp = healed;
        if (amount > 0) {
          events.push({
            kind: 'heal',
            side: defSideId,
            instanceId: defender.instanceId,
            amount,
            remainingHp: healed,
          });
        }
      }
      events.push({ kind: 'message', text: `It doesn't affect ${displayName(defender)}…` });
      return;
    }
  }

  // Offensive damage-multiplier abilities (Overgrow/Blaze/Torrent, Flash Fire).
  const atkHooks = getAbilityHooks(attacker.instance.abilityId);
  let otherMultiplier = 1;
  if (atkHooks.onModifyDamage) {
    otherMultiplier = atkHooks.onModifyDamage({
      user: attacker,
      move,
      multiplier: otherMultiplier,
    });
  }
  // Cozy↔Spicy combat handicap, folded into the same faithful damage-multiplier
  // hook (the formula in core/damage stays mainline-exact). In Cozy the foe deals
  // less and the player deals more; Mild/Spicy are neutral.
  otherMultiplier *= difficultyDamageMultiplier(deps, sideId);

  const critical = rollCritical(rng, 0);
  const ctx: DamageContext = {
    attackerStats: aStats,
    defenderStats: dStats,
    attackerTypes: aTypes,
    defenderTypes: dTypes,
    critical,
    otherMultiplier,
  };
  let dmg = computeDamage(attacker, defender, move, ctx, rng);

  // Sturdy: clamp a would-be OHKO from full HP to a 1-HP survivor.
  if (dmg >= defender.instance.currentHp && defHooks.onLethalDamage) {
    const save = defHooks.onLethalDamage({ defender, incomingDamage: dmg });
    if (save.survivesWith1Hp) {
      dmg = defender.instance.currentHp - 1;
    }
  }

  const applied = Math.min(dmg, defender.instance.currentHp);
  const remainingHp = defender.instance.currentHp - applied;
  defender.instance.currentHp = remainingHp;
  events.push({
    kind: 'damage',
    side: defSideId,
    instanceId: defender.instanceId,
    amount: applied,
    remainingHp,
    effectiveness,
    critical,
  });

  const madeContact = CONTACT_CATEGORIES.has(move.category);

  // Secondary effects only land if the target is still standing.
  if (remainingHp > 0) {
    applyMoveEffects(
      state,
      sideId,
      defSideId,
      attacker,
      defender,
      move,
      events,
      rng,
      deps,
      true,
      madeContact,
    );
    // On-contact defensive ability (e.g. Static paralysing the attacker).
    if (madeContact && defHooks.onContactHit) {
      const contact = defHooks.onContactHit({ defender, attacker, isContact: true, rng });
      if (contact) {
        inflictStatus(sideId, attacker, contact.status, events, rng, deps);
      }
    }
  }
}

/** Apply a move's declarative secondary effects (status / stat-change), gated by chance. */
function applyMoveEffects(
  state: BattleState,
  attackerSide: BattleSideId,
  defenderSide: BattleSideId,
  attacker: BattleParticipant,
  defender: BattleParticipant,
  move: MoveData,
  events: BattleEvent[],
  rng: Rng,
  deps: BattleDeps,
  _hit: boolean,
  _madeContact: boolean,
): void {
  if (!move.effects) return;
  for (const eff of move.effects) {
    // chance absent ⇒ always; otherwise roll int(100) < chance.
    if (eff.chance != null && rng.int(100) >= eff.chance) continue;

    if (eff.kind === 'inflictStatus') {
      const status = String(eff.params?.status ?? 'none') as StatusCondition;
      // Most damaging moves hit the foe; self-targeted boosts are rare in MVP data.
      const targetSide = move.target === 'self' ? attackerSide : defenderSide;
      const target = move.target === 'self' ? attacker : defender;
      inflictStatus(targetSide, target, status, events, rng, deps);
    } else if (eff.kind === 'statChange') {
      const stat = String(eff.params?.stat ?? 'atk');
      const stages = Number(eff.params?.stages ?? 0);
      const targetSide = move.target === 'self' ? attackerSide : defenderSide;
      const target = move.target === 'self' ? attacker : defender;
      applyStatStage(state, targetSide, target.instanceId, stat, stages, events);
    }
  }
}

/** Try to inflict a non-volatile status, respecting type immunities + existing status. */
function inflictStatus(
  targetSide: BattleSideId,
  target: BattleParticipant,
  status: StatusCondition,
  events: BattleEvent[],
  rng: Rng,
  deps: BattleDeps,
): void {
  if (status === 'none') return;
  const res = applyStatus(target.instance, status, typesOf(target, deps), rng);
  if (!res.applied) return;
  assignInstance(target, res.instance);
  events.push({ kind: 'status-applied', side: targetSide, instanceId: target.instanceId, status });
}

/** Adjust a participant's stat stage (clamped −6…+6) and emit a stat-change event. */
function applyStatStage(
  state: BattleState,
  sideId: BattleSideId,
  instanceId: string,
  stat: string,
  stages: number,
  events: BattleEvent[],
): void {
  const side = state.sides[sideId];
  const target = side.party.find((p) => p.instanceId === instanceId);
  if (!target || target.instance.currentHp <= 0 || stages === 0) return;
  const cur = numStage(target, stat);
  const nextStage = Math.max(-6, Math.min(6, cur + stages));
  if (nextStage === cur) return; // already pinned at the cap
  target.statStages = { ...target.statStages, [stat]: nextStage };
  events.push({ kind: 'stat-change', side: sideId, instanceId, stat, stages: nextStage - cur });
}

// ── Faints, replacement, end-of-turn ───────────────────────────────────────────

/** Detect faints on both active mons, emit faint + forced replacement, and end-check. */
function settleFaints(state: BattleState, events: BattleEvent[], deps: BattleDeps): void {
  if (state.finished) return;
  for (const sideId of ['player', 'opponent'] as const) {
    const side = state.sides[sideId];
    const active = activeOf(side);
    if (active && active.instance.currentHp <= 0 && !active.volatile.fainted) {
      active.volatile = { ...active.volatile, fainted: true };
      events.push({ kind: 'faint', side: sideId, instanceId: active.instanceId });
      // Forced replacement: send in the next usable party member, if any.
      const nextIdx = side.party.findIndex((p) => p.instance.currentHp > 0);
      if (nextIdx >= 0) {
        side.activeIndex = nextIdx;
        events.push({
          kind: 'switch',
          side: sideId,
          toInstanceId: side.party[nextIdx]!.instanceId,
        });
        fireSwitchIn(state, sideId, events, deps);
      }
    }
  }
  checkEnd(state, events);
}

/** Mark the battle finished + emit battle-end when a side has no usable mons. */
function checkEnd(state: BattleState, events: BattleEvent[]): void {
  if (state.finished) return;
  const playerAlive = sideHasUsable(state.sides.player);
  const oppAlive = sideHasUsable(state.sides.opponent);
  if (playerAlive && oppAlive) return;

  let winner: BattleSideId | 'draw';
  if (!playerAlive && !oppAlive) winner = 'draw';
  else if (!oppAlive) winner = 'player';
  else winner = 'opponent';

  state.finished = true;
  state.winner = winner;
  events.push({ kind: 'battle-end', winner, kindOfBattle: state.kind });
}

/** End-of-turn residual status damage + toxic escalation (via core/status). */
function applyEndOfTurn(state: BattleState, events: BattleEvent[]): void {
  const { damageEvents, updatedState } = endOfTurnEffects(state);
  // endOfTurnEffects only advances each side's ACTIVE instance (HP + toxic counter);
  // copy those advanced instances back onto our working participants in place so we
  // keep our volatile/statStages on the participant wrappers.
  for (const sideId of ['player', 'opponent'] as const) {
    const src = updatedState.sides[sideId];
    const advanced = src.party[src.activeIndex];
    const ours = state.sides[sideId].party[src.activeIndex];
    if (advanced && ours) assignInstance(ours, advanced.instance);
  }
  for (const de of damageEvents) {
    events.push({
      kind: 'damage',
      side: de.side,
      instanceId: de.instanceId,
      amount: de.amount,
      remainingHp: de.remainingHp,
      effectiveness: 1,
      critical: false,
    });
  }
}

// ── Small helpers ──────────────────────────────────────────────────────────────

function displayName(p: BattleParticipant): string {
  return p.instance.nickname ?? p.instance.speciesId;
}

function blockedMessage(reason: string): string {
  switch (reason) {
    case 'sleep':
      return 'It is fast asleep.';
    case 'freeze':
      return 'It is frozen solid!';
    case 'paralysis':
      return 'It is fully paralyzed and can’t move!';
    default:
      return 'It can’t move!';
  }
}

// ── Post-battle helpers (level-up / evolution are resolved by the caller) ───────

/** A defeated foe's EXP-relevant facts, surfaced for the post-battle leveling step. */
export interface PostBattleSummary {
  readonly winner: BattleSideId | 'draw';
  readonly caught: boolean;
  readonly fledOrRan: boolean;
}

/**
 * Summarize a finished battle for the post-battle pipeline (EXP award + level-up +
 * evolution live in core/leveling + core/evolution, not here). Returns null while the
 * battle is still in progress.
 */
export function summarizeBattle(
  state: BattleState,
  events: readonly BattleEvent[],
): PostBattleSummary | null {
  if (!state.finished || state.winner === undefined) return null;
  const caught = events.some((e) => e.kind === 'catch-attempt' && e.caught);
  const fledOrRan = state.winner === 'draw' && !caught;
  return { winner: state.winner, caught, fledOrRan };
}
